import { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Upload, FileText, X, CheckCircle2, Loader2 } from 'lucide-react'
import { useHistory } from 'react-router-dom'
import { useAdasIntelligenceStore } from '@caradasstore/AdasIntelligenceStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { getFriendlyErrorMessage } from '../../../utils/errorMessage'
import { FormModal, FormModalFooter } from '@components/branded-modal'

const MAX_BYTES = 25 * 1024 * 1024 // 25 MB

const stageLabel = {
  uploaded: 'Uploaded — waiting for scan...',
  polling: 'Scanning estimate for calibrations...',
  ready: 'Done — opening estimate...'
}

export const UploadEstimateModal = ({ open, onOpenChange }) => {
  const history = useHistory()
  const uploadEstimate = useAdasIntelligenceStore((s) => s.uploadEstimate)
  const ccId = useCenterStore((store) => store.ccId)

  const inputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [error, setError] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [stage, setStage] = useState(null)

  const reset = () => {
    setFile(null)
    setError(null)
    setIsSubmitting(false)
    setStage(null)
  }

  const validateFile = (f) => {
    if (!f) return 'Please select a file.'
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) return 'Only PDF files are supported.'
    if (f.size > MAX_BYTES) return 'File exceeds the 25 MB limit.'
    return null
  }

  const onPickFile = (f) => {
    const msg = validateFile(f)
    if (msg) {
      setError(msg)
      setFile(null)
      return
    }
    setError(null)
    setFile(f)
  }

  const onDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const f = e.dataTransfer.files?.[0]
    if (f) onPickFile(f)
  }

  const onSubmit = async () => {
    if (!file) {
      setError('Please drop or browse a PDF to upload.')
      return
    }
    setIsSubmitting(true)
    setError(null)
    try {
      const { estimateId, roNumber } = await uploadEstimate(file, {
        calibrationCenterId: ccId,
        onStatus: (s) => setStage(s.stage)
      })
      onOpenChange(false)
      reset()
      if (estimateId) {
        history.push(`/adas-intelligence/estimate/${estimateId}`)
      } else if (roNumber) {
        // Fall back to list view if we couldn't resolve the estimate id
        history.push('/adas-intelligence')
      }
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Upload failed'))
      setIsSubmitting(false)
      setStage(null)
    }
  }

  const handleOpenChange = (next) => {
    if (isSubmitting && !next) return // prevent accidental close during upload
    if (!next) reset()
    onOpenChange(next)
  }

  return (
    <FormModal
      open={open}
      onClose={() => handleOpenChange(false)}
      icon={Upload}
      title='Upload Estimate'
      subtitle='Drop a PDF estimate and ADAS Intelligence will identify all required calibrations.'
      width='max-w-[520px]'
      footer={
        <FormModalFooter
          onCancel={() => handleOpenChange(false)}
          cancelDisabled={isSubmitting}
          onConfirm={onSubmit}
          confirmDisabled={isSubmitting || !file}
          confirmLabel={
            isSubmitting ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Processing...
              </>
            ) : (
              <>
                <Upload className='h-4 w-4' />
                Upload &amp; Scan
              </>
            )
          }
        />
      }
    >
      <div className='space-y-4 py-2'>
        {/* Drop zone */}
        {!file ? (
          <div
            onDragOver={(e) => {
              e.preventDefault()
              setIsDragging(true)
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`flex h-[200px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              isDragging ? 'border-[#1F6FEB] bg-[#EFF6FF]' : 'border-[#D1D5D8] bg-[#F8FAFC] hover:border-[#6B7280]'
            }`}
          >
            <Upload className='mb-2 h-8 w-8 text-[#6B7280]' />
            <div className='text-sm font-semibold text-[#0A1628]'>Drop estimate PDF here</div>
            <div className='text-xs text-[#6B7280]'>or</div>
            <button type='button' className='mt-1 text-sm font-medium text-[#1F6FEB] hover:underline'>
              Browse files
            </button>
            <div className='mt-3 text-[11px] text-[#6B7280]'>
              PDF up to 25MB — Client, RO#, vehicle, and insurance extracted automatically
            </div>
            <input
              ref={inputRef}
              type='file'
              accept='application/pdf'
              className='hidden'
              onChange={(e) => onPickFile(e.target.files?.[0])}
            />
          </div>
        ) : (
          <div className='flex items-start gap-3 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-3'>
            <CheckCircle2 className='mt-0.5 h-5 w-5 flex-shrink-0 text-[#1F6FEB]' />
            <FileText className='mt-0.5 h-5 w-5 flex-shrink-0 text-[#1F6FEB]' />
            <div className='min-w-0 flex-1'>
              <div className='break-all text-sm font-medium leading-snug text-[#1E40AF]'>{file.name}</div>
              <div className='mt-0.5 text-xs text-[#1F6FEB]'>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
            </div>
            {!isSubmitting && (
              <button
                type='button'
                onClick={() => {
                  setFile(null)
                  setError(null)
                }}
                className='rounded p-1 text-[#1E40AF] hover:bg-[#DBEAFE]'
                aria-label='Remove file'
              >
                <X className='h-4 w-4' />
              </button>
            )}
          </div>
        )}

        {isSubmitting && stage && (
          <div className='flex items-center gap-2 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] px-3 py-2 text-sm text-[#1E40AF]'>
            <Loader2 className='h-4 w-4 animate-spin' />
            {stageLabel[stage] || 'Working...'}
          </div>
        )}

        {error && <div className='text-sm text-[#E24B4A]'>{error}</div>}
      </div>
    </FormModal>
  )
}

UploadEstimateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired
}
